import clsx from "clsx";
import { useCallback, useEffect, useRef, useState, RefObject } from "react";
import axios from "axios";
import Bowser from "bowser";
import { AnimatePresence, motion } from "framer-motion";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { CheckIcon, DevicePhoneMobileIcon } from "@heroicons/react/20/solid";
import { useMutation } from "@tanstack/react-query";
import {
	fetchPartnerInfo,
	getCurrentTime,
	postZap,
	sendEmail,
	useSearchEngineDetector,
} from "@/lib/utils";
import {
	employment_status,
	loan_type,
	living_status,
	VITE_ZEM_GATEWAY_URL,
	LOAN_TYPE_TRUCK,
	LOAN_TYPE_EQUIPMENT,
	LOAN_PURPOSE_PERSONAL,
	LOAN_PURPOSE_BUSINESS,
	EMP_STAT_SELF_EMPLOYED,
	BOAT_ASSET_TYPE_COMMERCIAL,
	business_loan_purpose,
	LOAN_TYPE_BUSINESS,
	LOAN_TYPE_PERSONAL,
	LOAN_TYPE_CARAVAN,
	LOAN_TYPE_BIKE,
	LOAN_TYPE_MOTORCYCLE,
	LOAN_PURPOSE_NOVATED_LEASE,
} from "@/lib/constants";
import {
	PersonalDetails,
	PhoneValidation,
	LoanDetails,
	AdditionalInfo,
	BulletSteps,
	LivingDetails,
} from "@/components/forms/parts/index";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";

function usePrevious<T>(value: T) {
	let ref = useRef<T>({} as T);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

type QuickFormProps = {
	className?: string;
	loanTypes?: string;
	siteTitle?: string;
	rootId?: HTMLElement;
};

export function QuickFormMain({ className, loanTypes, siteTitle, rootId }: QuickFormProps) {
	//Get values and setters from Zustand store
	const { formValues, setFormValue, resetFormValues, setFormError, formErrors } =
		useFormDataStore();
	const {
		setLoanAmountMin,
		selectedIndex,
		setSelectedIndex,
		isPhoneVerified,
		setPhoneVerified,
		enabledFields,
		setEnabledFields,
		setEnabledBusinessFields,
		enabledLoans,
		setEnabledLoans,
		isSubmitted,
		setIsSubmitted,
		validateFormDetails,
		formContainer,
		setFormContainer,
		toggleTC,
		setToggleTC,
		formError,
		stepProcess,
		setStepProcess,
		setHasAttemptedSubmit,
	} = useFormStateStore();

	const {
		applicationType,
		firstName,
		lastName,
		email,
		state,
		loanAmount,
		phone,
		loanPurpose,
		loanType,
		assetType,
		purchaseStage,
		assetAge,
		purchaseFrom,
		livingStatus,
		employmentStatus,
		abnStatus,
		gstStatus,
		combinedIncome,
		creditHistory,
	} = formValues;

	const [assetTypeID, setAssetTypeID] = useState(0);
	const [stepsMaxIndex, setStepsMaxIndex] = useState(stepProcess.length - 1);

	// Determining the form steps
	const getStepProcess = () => {
		setStepProcess("Default");
		setStepsMaxIndex(stepProcess.length - 1);
	};

	// Apply App Setting's Loan Type
	useEffect(() => {
		if (selectedIndex == 0) {
			const loans = loanTypes?.split(",").map((t) => t.trim()) || ["Car Loan"];
			if (loans.length === 1) {
				setFormValue("loanType", loans[0]);
				setEnabledFields(loans[0]); // Sets the allowed fields for a specific loan type.
				getStepProcess();
			}
			setEnabledLoans(loans); // Sets the allowed Loan Type options.
			getStepProcess();
		}
		if (
			[LOAN_PURPOSE_PERSONAL, LOAN_PURPOSE_NOVATED_LEASE].includes(formValues.loanPurpose) ||
			[LOAN_TYPE_PERSONAL, LOAN_TYPE_CARAVAN].includes(formValues.loanType)
		) {
			setFormValue("applicationType", "1"); // Personal/Consumer
		} else {
			setFormValue("applicationType", "2"); // Business/Commercial
		}
	}, [selectedIndex, setFormValue, applicationType, loanPurpose, stepsMaxIndex]);

	// Dynamically set the asset_type_id value
	useEffect(() => {
		if (formValues.loanType === LOAN_TYPE_BUSINESS) {
			// Business Loan
			const loanPurposeObject = business_loan_purpose.find(
				(type) => type.option === formValues.loanPurpose,
			);
			const asset_type_id = loanPurposeObject ? loanPurposeObject.id : 0;
			setAssetTypeID(Number(asset_type_id));
		} else {
			const loanTypeObject = loan_type.find((type) => type.option === loanType);
			const asset_type_id = loanTypeObject ? loanTypeObject.id : 0;
			setAssetTypeID(Number(asset_type_id));
		}
	}, [formValues.loanPurpose, loanType, assetTypeID]);

	/* Partner Info */
	const [isPartner, setIsPartner] = useState("No");
	const [partnerName, setPartnerName] = useState("");
	const [partnerId, setPartnerId] = useState("");
	const [partnerEmail, setPartnerEmail] = useState("");
	const [partnerWebsite, setPartnerWebsite] = useState("");
	const [partnerAssetType, setPartnerAssetType] = useState("");
	const hasFetched = useRef(false);

	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const [browserInfo, setBrowserInfo] = useState({});
	const timer = useRef<NodeJS.Timeout | undefined>(undefined);
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const [enabled, setEnabled] = useState(false); //Enables proceeding to the next page if T&A has been agreed.
	const prevIndex = usePrevious(selectedIndex);
	const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex;
	const sourceUrl = window.location.href;

	const residentialTypeObject = living_status.find((type) => type.value === livingStatus);
	const residential_status_id = residentialTypeObject ? residentialTypeObject.id : null;
	const employmentTypeObject = employment_status.find((type) => type.value === employmentStatus);
	const employment_status_id = employmentTypeObject ? employmentTypeObject.id : null;

	const headingRef = useRef<HTMLHeadingElement>(null);
	const navDivRef = useRef<HTMLDivElement | null>(null); //References to the Navigation Button's Div Container

	const isDisabled = (() => {
		if (selectedIndex === 0) {
			return !toggleTC || formError;
		}
		if (selectedIndex === 1) {
			return !isPhoneVerified || formError;
		}
		if (selectedIndex === 2) {
			return formError;
		}
		if (selectedIndex === 3) {
			return formError;
		}
		if (selectedIndex === 4) {
			return formError;
		}
		if (selectedIndex === stepsMaxIndex) {
			return formError;
		}
		return false;
	})();

	// For Navigation Buttons (Previous, Next, Submit)
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			let limiter = 600;

			setFormContainer(entry.contentRect.width);
			setIsNavDivSmall(entry.contentRect.width < limiter);
		});

		observer.observe(container);

		return () => observer.disconnect();
	}, [selectedIndex, formContainer]);

	const scrollToHeading = () => {
		if (window.matchMedia("(max-width: 767px)").matches) {
			setTimeout(() => {
				if (headingRef.current) {
					const additionalOffset = 120;
					const targetPosition =
						headingRef.current.getBoundingClientRect().top +
						window.scrollY -
						additionalOffset;

					window.scrollTo({
						top: targetPosition,
						behavior: "smooth",
					});
				}
			}, 100); // 100ms delay to ensure rendering
		}
	};

	async function getUserIP() {
		const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/getIP`);
		const data = await response.data;
		setUserIP(data.ip);
		setUsingVPN(data.vpn);
	}

	useEffect(() => {
		setSearchEngine(detectedSearchEngine);
	}, [detectedSearchEngine]);

	// Detect what browser and user IP
	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
	}, []);

	window.leadGenerated = window.leadGenerated || false;

	const loadDataLayerScript = () => {
		if (!window.leadGenerated) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "generate_lead",
				enhanced_conversion_data: {
					email: email,
					phone_number: "+61" + phone?.substring(1),
					first_name: firstName,
					last_name: lastName,
				},
			});
			window.leadGenerated = true;
		}
	};

	// Partner enabled pages
	useEffect(() => {
		if (hasFetched.current) return;
		hasFetched.current = true;

		const body = document.body;
		const classes = body.className.split(" ");
		// Fast Quote page template
		const partnerPagesClasses = ["partner-page-template"];
		const partner = partnerPagesClasses.some((cls) => classes.includes(cls));

		if (partner) {
			const postIdClass = classes.find((className) => className.startsWith("postid-"));
			const id = postIdClass?.replace("postid-", "") ?? "";

			if (!id) {
				console.error("No post ID found, skipping fetch.");
				return;
			}

			if (id === "954" || id === "946") {
				setLoanAmountMin(5000);
			}

			try {
				const getPartnerData = async () => {
					const response = await fetchPartnerInfo(id);

					if (response) {
						setPartnerName(response.title.rendered ?? "");
						setPartnerEmail(response.acf?.partner_email ?? "");
						setPartnerId(response.acf?.referral_id ?? "");
						setPartnerWebsite(response.acf?.partner_website ?? "");
						setPartnerAssetType(response.acf?.partner_asset_type ?? "");
					}
				};

				getPartnerData();
				setIsPartner("Yes");
			} catch (error) {
				console.error("Error fetching partner information:", error);
			}
		}
	}, []);

	const IP_STORAGE_KEY = "processedIPs";
	const EXPIRATION_TIME = 30 * 60 * 1000;

	const getProcessedIPs = (): { userIP: string; timestamp: number }[] => {
		const ips = localStorage.getItem(IP_STORAGE_KEY);
		return ips ? JSON.parse(ips) : [];
	};

	const addProcessedIP = (userIP: string) => {
		const ips = getProcessedIPs();
		const now = Date.now();
		const updatedIPs = ips.filter(({ timestamp }) => now - timestamp < EXPIRATION_TIME);
		updatedIPs.push({ userIP, timestamp: now });
		localStorage.setItem(IP_STORAGE_KEY, JSON.stringify(updatedIPs));
	};

	const isIPProcessed = (userIP: string): boolean => {
		const ips = getProcessedIPs();
		const now = Date.now();
		return ips.some(
			({ userIP: storedIP, timestamp }) =>
				storedIP === userIP && now - timestamp < EXPIRATION_TIME,
		);
	};

	const sendPreZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: assetTypeID,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? EMP_STAT_SELF_EMPLOYED
						: employmentStatus,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendPreZapMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: assetTypeID,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? EMP_STAT_SELF_EMPLOYED
						: employmentStatus,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: assetTypeID,
				residential_status_id: residential_status_id,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {
			resetFormValues();
			setEnabled(false);
		},
	});

	const sendMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const zapPost = {
				...formValues,
				dateSubmitted: getCurrentTime(),
				application_type_id: Number(applicationType),
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: assetTypeID,
				residential_status_id: residential_status_id,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) =>
						value !== applicationType &&
						value !== null &&
						value !== undefined &&
						value !== "",
				),
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {
			setEnabled(false);
		},
	});

	// Prevents Next button from being spammed causing skipping multiple pages.
	const [isPageLoading, setIsPageLoading] = useState(false);

	const next = useCallback(() => {
		if (isPageLoading) return;
		setHasAttemptedSubmit(true);

		let indexesWithValidation = [0, 2, 3, 4];

		if (indexesWithValidation.includes(selectedIndex)) {
			let validate = validateFormDetails;
			const isValid = validate?.();
			if (!isValid) return; // Stops next page if form errors exist.
		}

		if (selectedIndex < stepsMaxIndex) {
			setIsPageLoading(true);
			scrollToHeading();
			setSelectedIndex(selectedIndex + 1);
			setTimeout(() => setIsPageLoading(false), 500); // Add delay to give time for components to load.
		}
	}, [
		isPageLoading,
		selectedIndex,
		stepsMaxIndex,
		scrollToHeading,
		isPhoneVerified,
		formErrors,
		formError,
		setFormError,
		setHasAttemptedSubmit,
	]);

	const prev = useCallback(() => {
		if (selectedIndex === 1) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
			// For enabled multiple loan types, reset Loan Purpose and Asset Type in case loan type changes.
			if (enabledLoans.length > 1) {
				setFormValue("loanPurpose", "");
				setFormValue("assetType", "");
			}
		} else if (selectedIndex > 0 && selectedIndex < stepsMaxIndex) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
			if (
				// 86cyywnev - Single Loan Purpose Option does not need ABN Status cleared.
				enabledFields.loanPurpose?.length != 1
			) {
				setFormValue("abnStatus", "");
				setFormValue("gstStatus", "");
			}
		}
	}, [selectedIndex, stepsMaxIndex, scrollToHeading]);

	const close = useCallback(() => {
		setIsSubmitted(false);
		setPhoneVerified(false);
		resetFormValues();
		setSelectedIndex(0);
		setToggleTC(false);

		const $ = (window as any).jQuery;
		if (!$ || !siteTitle) return;

		const bodyClass = document.body.classList;
	}, [siteTitle, selectedIndex, stepsMaxIndex]);

	// Detect if tab is inactive
	useEffect(() => {
		const handleFocus = () => {
			setTabHasFocus(true);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};

		const handleBlur = () => {
			setTabHasFocus(false);
			timer.current = setTimeout(() => {
				// Handle the tab being inactive for 3 minutes here
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
			}, 180000); // 180000 milliseconds = 3 minutes
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		// Clean up
		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};
	}, [isPhoneVerified, isSubmitted, userIP]);

	// Detect when tab is reloaded and closed
	useEffect(() => {
		if (selectedIndex < stepsMaxIndex) {
			const handleBeforeUnload = (event: any) => {
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
				setPhoneVerified(false);
				setIsSubmitted(false);
				setSelectedIndex(0);
				resetFormValues();
			};

			window.addEventListener("beforeunload", handleBeforeUnload);
			window.addEventListener("pagehide", handleBeforeUnload);

			return () => {
				window.removeEventListener("beforeunload", handleBeforeUnload);
				window.removeEventListener("pagehide", handleBeforeUnload);
			};
		}
	}, [isPhoneVerified, isSubmitted, selectedIndex, userIP, stepsMaxIndex]);

	// Last Page, trigger Zap and Mail submission
	useEffect(() => {
		if (selectedIndex === stepsMaxIndex && isPhoneVerified && !isSubmitted) {
			sendMail.mutate();
			sendZap.mutate();
			setIsSubmitted(true);
			loadDataLayerScript();
		}
	}, [isPhoneVerified, selectedIndex, isSubmitted, stepsMaxIndex]);

	// Go to next page when phone is verified
	useEffect(() => {
		if (isPhoneVerified) {
			next();
			getUserIP();
		}
	}, [isPhoneVerified]);

	const handleKeyDown = useCallback((event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	}, []);

	// JQuery for getting the loan amount value field in the homepage and why jade page
	// Jade Truck have different page id for why jade page
	// Jade Finance and Jade Boat Loans why jade do not have the input field
	useEffect(() => {
		const $ = (window as any).jQuery;

		if ($) {
			const bodyClass = document.body.className;

			if (
				bodyClass.includes("home") ||
				bodyClass.includes("page-id-4454") ||
				bodyClass.includes("page-id-449")
			) {
				const updateLoanAmount = () => {
					const inputValue = $(".quick-quote-loan-amount").val() as string;

					if (inputValue) {
						let formattedValue = +inputValue.replace(/[^0-9]/g, "");
						setFormValue("loanAmount", formattedValue.toLocaleString());
					}
				};

				$(".quick-quote-loan-amount").on("change", updateLoanAmount);

				$("#gFTrigger").on("click", function () {
					updateLoanAmount();
				});

				if (selectedIndex === stepsMaxIndex) {
					$("#customModalClose").on("click", function () {
						setSelectedIndex(0);
						setIsSubmitted(false);
					});
				}
			}

			return () => {
				$(".quick-quote-loan-amount").off("change");
			};
		}
	}, [setFormValue, selectedIndex]);

	return (
		<div ref={navDivRef} className={clsx(className, "")}>
			{sendZap.isPending && (
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.2 }}
					className="eL-w-full eL-h-full eL-fixed eL-top-0 eL-left-0 eL-bg-slate-200 eL-bg-opacity-75 eL-z-50"
				>
					<div className="eL-flex eL-justify-center eL-items-center eL-h-full eL-w-full">
						<div role="status">
							<svg
								aria-hidden="true"
								className="eL-w-12 eL-h-12 eL-text-opacity-0 eL-text-white eL-animate-spin eL-bg-opacity-0 eL-fill-[--eL-theme-color]"
								viewBox="0 0 100 101"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
									fill="currentColor"
								/>
								<path
									d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
									fill="currentFill"
								/>
							</svg>
							<span className="eL-sr-only">Loading...</span>
						</div>
					</div>
				</motion.div>
			)}

			{/* Form */}
			<AnimateChangeInHeight>
				<form
					className="eL-pb-3 eL-pt-4 eL-px-4"
					onKeyDown={handleKeyDown}
					onSubmit={(e) => e.preventDefault()}
				>
					<AnimatePresence initial={false} mode="wait" custom={isForwards}>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="eL-space-y-4"
							>
								<FormHeading headingRef={headingRef} />
								<PersonalDetails
									enabled={enabled}
									setEnabled={setEnabled}
									loanType={loanType}
								/>
							</motion.div>
						)}

						{selectedIndex === 1 && (
							<motion.div
								key={1}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								{!isPhoneVerified ? (
									<>
										<OTPHeading headingRef={headingRef} />
										<PhoneValidation />
									</>
								) : (
									<>
										<FormVerified headingRef={headingRef} />
									</>
								)}
							</motion.div>
						)}

						{selectedIndex === 2 && (
							<motion.div
								key={2}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="eL-space-y-4"
							>
								<FormHeading headingRef={headingRef} />
								<LoanDetails />
							</motion.div>
						)}

						{selectedIndex === 3 && (
							<motion.div
								key={3}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="eL-space-y-4"
							>
								<FormHeading headingRef={headingRef} />
								<LivingDetails />
							</motion.div>
						)}

						{selectedIndex === 4 && (
							<motion.div
								key={4}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="eL-space-y-4"
							>
								<FormHeading headingRef={headingRef} />
								<AdditionalInfo />
							</motion.div>
						)}

						{selectedIndex === stepsMaxIndex && (
							<motion.div
								key={stepsMaxIndex}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading headingRef={headingRef} />
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex < stepsMaxIndex ? (
				<div
					className={clsx(
						isNavDivSmall ? "eL-flex-wrap" : "eL-flex-nowrap",
						"eL-flex eL-items-center eL-justify-center eL-border-t eL-border-gray-200 eL-bg-white eL-mt-2 eL-py-3 eL-px-4",
					)}
				>
					<BulletSteps
						steps={stepProcess}
						selectedIndex={selectedIndex}
						navRef={isNavDivSmall}
					/>
					<div
						className={clsx(
							selectedIndex === stepsMaxIndex
								? "eL-hidden"
								: "eL-flex eL-flex-1 eL-justify-end",
						)}
					>
						<button
							disabled={selectedIndex === 1 && !isPhoneVerified}
							className={clsx(
								selectedIndex === 0 || (selectedIndex === 1 && !isPhoneVerified)
									? "eL-hidden"
									: isNavDivSmall
										? "eL-w-full"
										: "eL-w-auto",
								"eL-bg-white hover:eL-bg-gray-50 eL-relative eL-inline-flex eL-items-center eL-rounded-md eL-justify-center eL-px-3 eL-py-2 eL-text-md eL-font-semibold eL-text-gray-900 eL-ring-1 eL-ring-inset eL-ring-gray-300 focus-visible:eL-outline-offset-0",
							)}
							onClick={() => prev()}
						>
							Previous
						</button>
						<button
							className={clsx(
								isDisabled
									? "eL-bg-gray-100 hover:eL-cursor-not-allowed eL-text-gray-400 eL-ring-gray-100"
									: isNavDivSmall
										? "eL-bg-[--eL-theme-color] hover:eL-bg-[--eL-hover-color] eL-w-full"
										: "eL-bg-[--eL-theme-color] hover:eL-bg-[--eL-hover-color] eL-w-auto",
								"eL-relative eL-ml-3 eL-inline-flex eL-items-center eL-rounded-md eL-justify-center eL-px-3 eL-py-2 eL-text-md eL-font-semibold eL-text-white eL-ring-1 eL-ring-inset eL-ring-[--eL-theme-color] focus-visible:eL-outline-offset-0",
							)}
							onClick={() => {
								if (selectedIndex !== 1 || isPhoneVerified) {
									next();
								}
							}}
						>
							{selectedIndex === stepsMaxIndex - 1 ? "Submit" : "Next"}
						</button>
					</div>
				</div>
			) : (
				<div className="eL-flex eL-items-center eL-flex-wrap eL-justify-center eL-border-t eL-border-gray-200 eL-bg-white eL-mt-2 eL-px-4 eL-py-3 sm:eL-px-6 sm:eL-justify-between">
					<button
						className="eL-bg-[--eL-theme-color] hover:eL-bg-[--eL-hover-color] eL-relative eL-mb-2 eL-mx-auto eL-block eL-w-1/3 eL-text-center eL-rounded-md eL-px-3 eL-py-2 text-md eL-font-semibold eL-text-white eL-ring-1 eL-ring-inset eL-ring-[--eL-theme-color] focus-visible:eL-outline-offset-0"
						onClick={() => {
							close();
						}}
					>
						Close
					</button>
				</div>
			)}
		</div>
	);
}

function OTPHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "eL-relative")}>
				<div className="eL-mx-auto eL-flex eL-h-20 eL-w-20 eL-items-center eL-justify-center eL-rounded-full eL-bg-[--eL-bullet-bg]">
					<DevicePhoneMobileIcon
						className="eL-h-10 eL-w-10 eL-text-[--eL-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="eL-mt-3 eL-text-center sm:eL-mt-5">
					<h3
						ref={headingRef}
						className="eL-text-xl eL-font-semibold eL-leading-8 eL-text-gray-900"
					>
						Verify Your Number
					</h3>
				</div>
			</div>
		</>
	);
}

function FormHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "eL-relative")}>
				<div className=" eL-text-center">
					<h2
						ref={headingRef}
						className="eL-text-[2.2rem] eL-mb-3 eL-font-semibold eL-leading-8 eL-text-[--eL-theme-h-text]"
					>
						Get a Quick Quote
					</h2>
					<div className="eL-mt-2">
						<p className="eL-text-md eL-text-gray-500">
							This will not affect your credit score
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

function FormSuccessHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "eL-relative")}>
				<div className="eL-mx-auto eL-flex eL-h-20 eL-w-20 eL-items-center eL-justify-center eL-rounded-full eL-bg-[--eL-bullet-bg]">
					<CheckIcon
						className="eL-h-10 eL-w-10 eL-text-[--eL-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="eL-mt-3 eL-text-center sm:eL-mt-5">
					<h3
						ref={headingRef}
						className="eL-text-[2rem] eL-mb-3 eL-font-semibold eL-leading-8 eL-text-[--eL-theme-h-text]"
					>
						Successfully Submitted
					</h3>
					<div className="eL-mt-5 eL-mb-5">
						<p className="eL-text-md eL-text-gray-500">
							Thanks for contacting us! We will get in touch with you shortly.
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

function FormVerified({
	className,
	headingRef,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "eL-relative")}>
				<div className="eL-mx-auto eL-flex eL-h-20 eL-w-20 eL-items-center eL-justify-center eL-rounded-full eL-bg-[--eL-bullet-bg]">
					<CheckIcon
						className="eL-h-10 eL-w-10 eL-text-[--eL-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="eL-mt-3 eL-text-center sm:eL-mt-5">
					<h3
						ref={headingRef}
						className="eL-text-[2rem] eL-mb-3 eL-font-semibold eL-leading-8 eL-text-[--eL-theme-h-text]"
					>
						Thank you!
					</h3>
					<div className="eL-mt-5 eL-mb-5">
						<p className="eL-text-md eL-text-gray-500">Successfully verified</p>
					</div>
				</div>
			</div>
		</>
	);
}
